import React, { useCallback, useRef } from 'react'
import './CalculationResult.css';
import { DataGrid, Column, DataGridRef } from 'devextreme-react/data-grid';
import { RateCalculation } from './Models';
import { getBankingDayConventionDropdown, formatStringDate, getLocale, translate as t, formatPercent, formatFixedPoint } from './i18n';
import { exportDataGrid } from 'devextreme/excel_exporter';
import { Button } from 'devextreme-react/button';

import { Workbook } from "exceljs"
import { saveAs } from "file-saver"

interface IProps {
  calculation: RateCalculation;
  className?: string;
}

const locale = getLocale();

const CalculationResult: React.FC<IProps> = ({ calculation, className }) => {
  // https://js.devexpress.com/Demos/WidgetsGallery/Demo/DataGrid/Overview/React/Light/

  const { rounding } = calculation.query;

  const hasModifiedFrom = calculation.effectiveRatePeriodStart !== calculation.query.from;
  const hasModifiedTo = calculation.effectiveRatePeriodEnd !== calculation.query.to;

  const hasModified = hasModifiedFrom || hasModifiedTo;
  const bankingDaysConvention = getBankingDayConventionDropdown(locale);
  const bankingDaysConventionTranslated = hasModified
    ? bankingDaysConvention
      .filter(kv => kv.value === calculation?.query?.bankingDayConvention)
      .map(kv => kv.name)
    : "";


  const gridRef = useRef<DataGridRef>();

  const exportResultToExcel = useCallback(() => {
    let workbook = new Workbook();
    let worksheet = workbook.addWorksheet("Steps");

    exportDataGrid({
      component: gridRef.current?.instance(),
      worksheet: worksheet
    }).then(function () {
      workbook.xlsx.writeBuffer().then(function (buffer) {
        saveAs(
          new Blob([buffer], { type: "application/octet-stream" }),
          "nowa_calculation.xlsx"
        );
      });
    });
  }, []);

  return <div className={className}>
    <div style={{ margin: "0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
      <h2>{t("table.title")}</h2> <Button stylingMode='text' icon='xlsxfile' onClick={exportResultToExcel} text={t("table.export")} />
    </div>
    <div className='calculation-result-overview'>
      <div>
        <div title={t("table.effectiveRatePeriodStart.hint")} className='table-row'>
          <label>{t("table.effectiveRatePeriodStart")}</label> 
          <div className='folding-entries'>
            {hasModifiedFrom ? <div className="red-strike-through">{formatStringDate(calculation.query.from)}</div> : null} <div>{formatStringDate(calculation?.effectiveRatePeriodStart)}</div></div>
        </div>
        <div title={t("table.effectiveRatePeriodEnd.hint")} className='table-row'>
          <label>{t("table.effectiveRatePeriodEnd")}</label> 
          <div className='folding-entries'>
            {hasModifiedTo ? <div className="red-strike-through">{formatStringDate(calculation.query.to)}</div> : null} <div>{formatStringDate(calculation?.effectiveRatePeriodEnd)}</div></div>
        </div>
        <div title={t("table.annualRate.hint")} className='table-row'>
          <label>{t("table.annualRate")}</label> <span>{formatPercent(calculation?.annualRate, rounding)}</span>
        </div>
        <div title={t("table.totalRate.hint")} className='table-row'>
          <label>{t("table.totalRate")}</label> <span>{formatPercent(calculation?.totalRate, rounding)}</span>
        </div>
      </div>

      <div>
        <div title={t("table.accruedInterest.hint")} className='table-row'>
          <label>{t("table.accruedInterest")}</label> <span>{formatFixedPoint(calculation?.payment)} NOK</span>
        </div>
        <div title={t("table.daysObservationPeriod.hint")} className='table-row'>
          <label>{t("table.daysObservationPeriod")}</label> <span>{calculation?.observationPeriodDaysCount}</span>
        </div>
        <div title={t("table.daysRatePeriod.hint")} className='table-row'>
          <label>{t("table.daysRatePeriod")}</label> <span>{calculation?.calendarDaysCount}</span>
        </div>
        <div title={t("table.settlementDate.hint")} className='table-row'>
          <label>{t("table.settlementDate")}</label> <span>{formatStringDate(calculation?.maturityDate)}</span>
        </div>
      </div>
    </div>
    {(hasModified) ? <p className='red-text' style={{ margin: "0" }}>* {t("table.adjustedWithBankDayConvention")} '{bankingDaysConventionTranslated}'</p> : null}

      <DataGrid columnAutoWidth={true} className='calculation-result-steps' ref={gridRef} showBorders={true} dataSource={calculation?.steps} keyExpr="rateDate" rowAlternationEnabled={true} keyboardNavigation={{ enabled: false }} paging={{ enabled: false }}>
      <Column dataField="rateDate" caption={t("table.rateDate")} dataType="date" format={{ year: 'numeric', month: '2-digit', day: '2-digit' } as any} width="auto" allowSorting={false} />
      <Column dataField="observationDate" caption={t("table.observationDate")} dataType="date" format={{ year: 'numeric', month: '2-digit', day: '2-digit' } as any} width="auto" allowSorting={false} />
      <Column dataField="rate" caption={`${t("table.rate")} (%)`} dataType="number" format={"#0.00"} allowSorting={false} />
      <Column dataField="appliedRate" caption={`${t("table.appliedRate")} (%)`} format={"#0.00"} dataType="number" width="auto" allowSorting={false} />
      <Column dataField="calendarDayCount" caption={t("table.calendarDayCount")} dataType="number" width="auto" allowSorting={false} />
      <Column dataField="compoundedFactor" caption={t("table.compoundingFactor")} format={"#0.0000000000"} dataType="number" width="auto" allowSorting={false} visible={true} />
      <Column dataField="principal" caption={`${t("table.principal")} (NOK)`} dataType="number" format={{ type: "fixedPoint", precision: 2 }} width="auto" allowSorting={false} />
    </DataGrid>
  </div>
}

export default CalculationResult;